"use client";

import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FiUpload, FiCheck, FiList, FiRefreshCw } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tab } from "@headlessui/react";
import CustomInput from "./CustomInput";
import CustomTextArea from "./CustomTextArea";
import CustomSelect from "./CustomSelect";
import CustomButton from "./CustomButton";

export default function EssayGrader() {
  const baseURL = process.env.REACT_APP_BASE_URL;

  const [activeTab, setActiveTab] = useState(0);
  const [essays, setEssays] = useState([]);
  const [essayFilters, setEssayFilters] = useState({
    teacherid: "",
    studentid: "",
  });
  const [parseEssayData, setParseEssayData] = useState({
    teacher: "",
    student: "",
    handwritten: "-1",
    paperType: "4",
    numQuestions: "-1",
    moreInfo: "[]",
    pageNumbers: "[]",
    file: null,
  });
  const [gradeEssayId, setGradeEssayId] = useState("");

  const fetchEssays = async () => {
    try {
      const params = {};
      if (essayFilters.teacherid) params.teacherid = essayFilters.teacherid;
      if (essayFilters.studentid) params.studentid = essayFilters.studentid;

      const response = await axios.get(`${baseURL}/getessays/`, { params });
      setEssays(response.data.essays || []);
      toast.success("Essays fetched successfully!");
    } catch (error) {
      console.error("Error fetching essays:", error);
      toast.error("Failed to fetch essays. Please try again.");
    }
  };

  const handleParseEssay = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();

      const payload = {
        ...parseEssayData,
        teacher: String(parseEssayData.teacher),
        student: String(parseEssayData.student),
        handwritten: String(parseEssayData.handwritten),
        numQuestions: String(parseEssayData.numQuestions),
      };

      payload.moreInfo = JSON.stringify([
        {
          question: 0,
          max_marks: 10,
          max_words: 100,
          special_instructions: "Follow the guidelines",
          model_solution: "This is a model solution",
          candq: "What is this?",
        },
      ]);

      payload.pageNumbers = JSON.stringify([
        {
          question: 0,
          startPage: 1,
          endPage: 2,
        },
      ]);

      Object.entries(payload).forEach(([key, value]) => {
        if (key === "file") {
          if (value) formData.append(key, value);
        } else {
          formData.append(key, value);
        }
      });

      const response = await axios.post(`${baseURL}/parseessay/`, formData);
      if (response.data.status === "success") {
        toast.success(
          `Essay parsed successfully! Essay ID: ${response.data.essay_id}`
        );
        console.log("Essay data:", response.data.essay_data);
      } else {
        toast.error("Error: " + response.data.message);
      }
    } catch (error) {
      toast.error("Error parsing essay: " + error.message);
    }
  };

  const handleGradeEssay = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("essay_id", gradeEssayId);

      const response = await axios.post(`${baseURL}/gradeessay/`, formData);
      if (response.data.status === "success") {
        toast.success(`Essay ${response.data.essay_id} graded successfully!`);
        console.log("Updated essay data:", response.data.essay_data);
        fetchEssays();
      } else {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("Error grading essay: " + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-300 via-amber-500 to-amber-700 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden"
      >
        <div className="p-8">
          <h1 className="text-4xl font-extrabold text-center mb-8 text-gray-900">
            Essay Grader Platform
          </h1>
          <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
            <Tab.List className="flex p-1 space-x-1 bg-blue-900/20 rounded-xl mb-8">
              {["Parse Essay", "Grade Essay", "Essay List"].map((tab, idx) => (
                <Tab
                  key={idx}
                  className={({ selected }) =>
                    `w-full py-2.5 text-sm leading-5 font-medium text-blue-700 rounded-lg focus:outline-none focus:ring-2 ring-offset-2 ring-offset-blue-400 ring-white ring-opacity-60 ${
                      selected
                        ? "bg-white shadow"
                        : "text-blue-100 hover:bg-white/[0.12] hover:text-white"
                    }`
                  }
                >
                  {tab}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel>
                <form onSubmit={handleParseEssay} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <CustomInput
                      label="Teacher ID"
                      type="number"
                      value={parseEssayData.teacher}
                      onChange={(e) =>
                        setParseEssayData((prev) => ({
                          ...prev,
                          teacher: e.target.value,
                        }))
                      }
                      required
                    />
                    <CustomInput
                      label="Student ID"
                      type="number"
                      value={parseEssayData.student}
                      onChange={(e) =>
                        setParseEssayData((prev) => ({
                          ...prev,
                          student: e.target.value,
                        }))
                      }
                      required
                    />
                    <CustomSelect
                      label="Handwritten"
                      value={parseEssayData.handwritten}
                      onChange={(e) =>
                        setParseEssayData((prev) => ({
                          ...prev,
                          handwritten: e.target.value,
                        }))
                      }
                      options={[
                        { value: "-1", label: "Ambiguous" },
                        { value: "0", label: "Typed" },
                        { value: "1", label: "Handwritten" },
                      ]}
                    />
                    <CustomInput
                      label="Paper Type"
                      type="text"
                      value={parseEssayData.paperType}
                      onChange={(e) =>
                        setParseEssayData((prev) => ({
                          ...prev,
                          paperType: e.target.value,
                        }))
                      }
                    />
                    <CustomInput
                      label="Number of Questions"
                      type="number"
                      value={parseEssayData.numQuestions}
                      onChange={(e) =>
                        setParseEssayData((prev) => ({
                          ...prev,
                          numQuestions: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <CustomTextArea
                    label="More Info"
                    value={parseEssayData.moreInfo}
                    onChange={(e) =>
                      setParseEssayData((prev) => ({
                        ...prev,
                        moreInfo: e.target.value,
                      }))
                    }
                    placeholder='[{"question": 1, "metadata": "example"}]'
                  />
                  <CustomTextArea
                    label="Page Numbers"
                    value={parseEssayData.pageNumbers}
                    onChange={(e) =>
                      setParseEssayData((prev) => ({
                        ...prev,
                        pageNumbers: e.target.value,
                      }))
                    }
                    placeholder='[{"question": 1, "startPage": 1, "endPage": 2}]'
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      File
                    </label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) =>
                        setParseEssayData((prev) => ({
                          ...prev,
                          file: e.target.files[0],
                        }))
                      }
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-full file:border-0
                        file:text-sm file:font-semibold
                        file:bg-violet-50 file:text-violet-700
                        hover:file:bg-violet-100"
                      required
                    />
                  </div>
                  <CustomButton type="submit" icon={<FiUpload />}>
                    Parse Essay
                  </CustomButton>
                </form>
              </Tab.Panel>
              <Tab.Panel>
                <form onSubmit={handleGradeEssay} className="space-y-6">
                  <CustomInput
                    label="Essay ID"
                    type="number"
                    value={gradeEssayId}
                    onChange={(e) => setGradeEssayId(e.target.value)}
                    required
                  />
                  <CustomButton type="submit" icon={<FiCheck />}>
                    Grade Essay
                  </CustomButton>
                </form>
              </Tab.Panel>
              <Tab.Panel>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <CustomInput
                      label="Filter by Teacher ID"
                      type="number"
                      value={essayFilters.teacherid}
                      onChange={(e) =>
                        setEssayFilters((prev) => ({
                          ...prev,
                          teacherid: e.target.value,
                        }))
                      }
                    />
                    <CustomInput
                      label="Filter by Student ID"
                      type="number"
                      value={essayFilters.studentid}
                      onChange={(e) =>
                        setEssayFilters((prev) => ({
                          ...prev,
                          studentid: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <CustomButton onClick={fetchEssays} icon={<FiRefreshCw />}>
                    Refresh Essays
                  </CustomButton>
                  <div className="mt-6 space-y-4 max-h-96 overflow-y-auto">
                    {essays.map((essay, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className="bg-white p-4 rounded-lg shadow"
                      >
                        <h3 className="text-lg font-semibold mb-2 text-gray-900">
                          Essay {essay.id}
                        </h3>
                        <pre className="whitespace-pre-wrap text-sm text-gray-600 overflow-x-auto">
                          {JSON.stringify(essay, null, 2)}
                        </pre>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </motion.div>
      <ToastContainer position="bottom-right" />
    </div>
  );
}
